import { Column, Text } from "@stenajs-webui/core";
import { TextInput } from "@stenajs-webui/forms";
import type { FC } from "react";

import type { BasicBooking } from "../../../../../common/graphql/fragments/gql/BasicBooking";
import type { TrackerCategory } from "../../../../../common/tracking/trackerService";
import { useUpdateField } from "../../../../../common/update-information/hooks/useUpdateField";
import { exhaustSwitchCaseElseNull } from "../../../../../common/utils/ExhaustSwitchCase";
import { useUpdateFormStateMinimalBooking } from "../../../../../common/utils/minimalBooking";
import { ImportExportType } from "../../../../../gql/graphql";
import { shouldShowEnsLinkTogetherWithEnvelopeId } from "../../../../../services/ens/utils";
import {
  getImportExportSettingsFromMinimalBooking,
  getImportExportSettingsFromRoute,
} from "../../../../import-export/util/ImportExportSettingsGetters";
import {
  transformUpdateFieldToTrackerAction,
  useTrackingTextInput,
} from "../../../../simple-create/hooks/UseTrackingTextInput";
import { getEnvelopeIdPlaceholder } from "../../../grid/components/grid-cells/import-export/utils/envelopeIdUtil";
import { BookingUpdateEnsLink } from "../BookingUpdateEnsLink";

interface ImportExportFieldProps {
  booking: BasicBooking;
  disabled?: boolean;
  id?: string;
  name: "importReference" | "exportReference";
}

const trackerCategory: TrackerCategory = "Details";

export const ImportExportField: FC<ImportExportFieldProps> = ({
  booking,
  name,
  disabled,
  id,
}) => {
  const { onBlur, onFocus } = useTrackingTextInput(
    transformUpdateFieldToTrackerAction(name),
    trackerCategory,
  );

  const minimalBooking = useUpdateFormStateMinimalBooking();

  const importExportSettings = getImportExportSettingsFromMinimalBooking(
    minimalBooking,
    getImportExportSettingsFromRoute(minimalBooking?.route, name),
  );

  const { editedValue, initialValue, onChange } = useUpdateField<
    "importReference" | "exportReference"
  >(name);

  if (
    name === "importReference" &&
    minimalBooking?.route?.useCustomsClassification &&
    minimalBooking?.customsClassificationCode == null
  ) {
    return (
      <TextInput
        disabled
        disableContentPaddingLeft
        placeholder={"Select customs proc."}
        width={"100%"}
        value={editedValue}
        id={id}
      />
    );
  }

  if (
    !importExportSettings ||
    importExportSettings.type === ImportExportType.Empty
  ) {
    return (
      <TextInput
        disabled
        disableContentPaddingLeft
        width={"100%"}
        value={editedValue}
        id={id}
      />
    );
  }

  if (importExportSettings.type === ImportExportType.Scac) {
    return (
      <Text>
        {name === "importReference"
          ? booking.importScacId
          : booking.exportScacId}
      </Text>
    );
  }

  if (importExportSettings.type === ImportExportType.Ens) {
    return (
      <BookingUpdateEnsLink
        booking={booking}
        optional={!importExportSettings.ensMandatory}
      />
    );
  }

  if (importExportSettings.type === ImportExportType.EnvelopeId) {
    return (
      <Column gap>
        <TextInput
          disabled={disabled}
          disableContentPaddingLeft
          placeholder={getEnvelopeIdPlaceholder(importExportSettings)}
          width={"100%"}
          onValueChange={onChange}
          value={editedValue}
          style={{
            fontStyle: editedValue !== initialValue ? "italic" : "normal",
            fontWeight: editedValue !== initialValue ? "bold" : "normal",
          }}
          onBlur={onBlur}
          onFocus={onFocus}
          id={id}
        />
        {shouldShowEnsLinkTogetherWithEnvelopeId(
          booking,
          importExportSettings,
        ) && (
          <BookingUpdateEnsLink
            booking={booking}
            optional={!importExportSettings.ensMandatory}
          />
        )}
      </Column>
    );
  }

  return exhaustSwitchCaseElseNull(importExportSettings.type);
};
